<script setup lang="ts">
import { computed } from 'vue';
import { closeOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

export interface ConfirmData {
  title: string;
  subtitle: string;
  btnLabel: string;
  color: string;
  icon?: string;
  cancelBtnLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

const { t } = useI18n();

const props = defineProps<{
  confirmData: ConfirmData | null;
  open: boolean;
}>();
const emit = defineEmits<{
  close: [];
}>();

const isOpen = computed({
  get: () => !!props.open,
  set: value => {
    if (!value) {
      emit('close');
    }
  },
});

const handleConfirm = async () => {
  try {
    await props.confirmData?.onConfirm();
  } finally {
    emit('close');
  }
};

const handleCancel = async () => {
  if (props.confirmData?.onCancel) {
    await props.confirmData?.onCancel();
  }
  emit('close');
};
</script>

<template>
  <ion-modal :is-open="isOpen" :show-backdrop="true" :backdrop-dismiss="false" class="p-4">
    <ion-card class="modal-content bg-medium-dark m-0">
      <ion-card-header class="flex flex-col text-tertiary mb-4" :class="{ 'text-center': confirmData?.icon }">
        <div class="close-btn ml-auto">
          <ion-icon @click="emit('close')" :icon="closeOutline" class="h-6 w-6" />
        </div>
        <div v-if="confirmData?.icon" class="icon-container p-4 mx-auto mb-4">
          <ion-icon :icon="confirmData?.icon" :class="`text-5xl text-${confirmData?.color}`" />
        </div>
        <ion-label class="text-h4 p-0 mb-2">{{ confirmData?.title }}</ion-label>
        <ion-text class="text-p">{{ confirmData?.subtitle }}</ion-text>
      </ion-card-header>

      <ion-card-content class="text-tertiary w-full">
        <div class="flex flex-col space-y-4">
          <base-button shape="round" flat :color="confirmData?.color" @click="handleConfirm">
            {{ confirmData?.btnLabel }}
          </base-button>
          <base-button class="cancel-btn text-tertiary" shape="round" fill="clear" @click="handleCancel">{{
            confirmData?.cancelBtnLabel || t('confirm-popup.cancel')
          }}</base-button>
        </div>
      </ion-card-content>
    </ion-card>
  </ion-modal>
</template>

<style lang="scss" scoped>
@import '@/scss/setup/_variables.scss';

.modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .icon-container {
    background-color: #2c2c2c;
    border-radius: 0.25rem;
  }

  .base-button {
    --box-shadow: none;
  }

  .cancel-btn {
    --border-color: var(--ion-color-tertiary);
    --border-style: solid;
    --border-width: 1px;
  }
}

ion-modal {
  --box-shadow: none;

  --width: auto;
  --height: auto;
  --border-radius: 0.5rem; /* Optional: for rounded corners */
  --background: #2c2c2c;
}
</style>
